// src/components/Notifications/UnifiedNotificationCenter.jsx
// 
// FIXED VERSION - Updated to work correctly with v2 Notifications API
// 
// Key Changes:
// 1. Changed params from URLSearchParams string to plain object
// 2. Updated unread filter to use boolean instead of string '1'
// 3. Maintains all existing functionality while being v2 compatible
// 
// This component now works seamlessly with the updated API.notifications interface
// which defaults to v2 endpoints.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Menu,
  MenuItem,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Badge,
  Divider,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  FilterList as FilterListIcon,
  Notifications as NotificationsIcon,
  Campaign as CampaignIcon,
  Handshake as HandshakeIcon,
  Message as MessageIcon,
  AlternateEmail as MentionIcon,
  AttachMoney as MoneyIcon,
  Description as ContractIcon,
  FolderShared as OrganizerIcon,
  Announcement as SystemIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import API from '../../API';

// ============================================================================
// ICON MAPPING
// ============================================================================
const NOTIFICATION_ICONS = {
  // Campaigns
  campaign_created: CampaignIcon,
  campaign_updated: CampaignIcon,
  campaign_invitation: CampaignIcon,
  campaign_accepted: CheckCircleIcon,
  
  // Partnerships
  partnership_created: HandshakeIcon,
  partnership_invitation: HandshakeIcon,
  partnership_accepted: CheckCircleIcon,
  
  // Messages
  new_message: MessageIcon,
  new_conversation: MessageIcon,
  conversation_mention: MentionIcon,
  
  // Financial
  payout_completed: MoneyIcon,
  invoice_paid: MoneyIcon,
  blitzpay_received: MoneyIcon,
  
  // Contracts
  contract_pending: ContractIcon,
  contract_signed: CheckCircleIcon,
  
  // Organizers
  organizer_shared: OrganizerIcon,
  checklist_item_added: OrganizerIcon,
  
  // Social
  new_follower: StarIcon,
  
  // System
  system_announcement: SystemIcon,
};

const SOURCE_ICONS = {
  campaign: CampaignIcon,
  partnership: HandshakeIcon,
  conversation: MessageIcon,
  financial: MoneyIcon,
  contract: ContractIcon,
  organizer: OrganizerIcon,
  system: SystemIcon,
};

// ============================================================================
// PRIORITY COLORS
// ============================================================================
const PRIORITY_COLORS = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  urgent: 'error',
};

// ====================================
// MAIN COMPONENT
// ====================================
export default function UnifiedNotificationCenter({
  anchorEl,
  onClose,
  isCreator = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedTab, setSelectedTab] = useState('all');
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  
  const open = Boolean(anchorEl);
  
  // ──────────────────────────────────────────────────────────────────────
  // FETCH NOTIFICATIONS - FIXED FOR V2 API
  // ──────────────────────────────────────────────────────────────────────
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['notifications', 'v2', selectedTab, showOnlyUnread],
    async () => {
      // Build params as object for v2 API (not URLSearchParams)
      const params = {};
      
      // V2 API expects boolean for unread filter
      if (showOnlyUnread) {
        params.unread = true;
      }
      
      // Filter by source if not showing all
      if (selectedTab !== 'all') {
        params.source = selectedTab;
      }
      
      // V2 API returns array directly or wrapped in data property
      const response = await API.notifications.listAll(params);
      return response.data || response;
    },
    {
      enabled: open,
      staleTime: 1000 * 30, // 30 seconds
    }
  );
  
  // ──────────────────────────────────────────────────────────────────────
  // FETCH COUNTS - V2 API
  // ──────────────────────────────────────────────────────────────────────
  const { data: counts } = useQuery(
    ['notifications', 'v2', 'counts'],
    async () => {
      const response = await API.notifications.getCounts();
      return response.data || response;
    },
    {
      refetchInterval: 30000,
    }
  );
  
  // ──────────────────────────────────────────────────────────────────────
  // MUTATIONS - V2 API
  // ──────────────────────────────────────────────────────────────────────
  const invalidateAllNotificationQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'], exact: false });
  }, [queryClient]);

  const markReadMutation = useMutation(
    (notificationId) => API.notifications.markAsRead(notificationId),
    { onSuccess: invalidateAllNotificationQueries }
  );
  
  const markAllReadMutation = useMutation(
    () => API.notifications.markAllRead(),
    { onSuccess: invalidateAllNotificationQueries }
  );
  
  const dismissMutation = useMutation(
    (notificationId) => API.notifications.dismiss(notificationId),
    { onSuccess: invalidateAllNotificationQueries }
  );
  
  const deleteMutation = useMutation(
    (notificationId) => API.notifications.delete(notificationId),
    { onSuccess: invalidateAllNotificationQueries }
  );
  
  // ──────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ──────────────────────────────────────────────────────────────────────
  // src/components/Notifications/UnifiedNotificationCenter.jsx
// Update the handleNotificationClick function:

const handleNotificationClick = useCallback(
  (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    
    // Navigate to the notification's route
    if (notification.frontend_route) {
      // Check if it's an absolute URL (external link)
      if (notification.frontend_route.startsWith('http://') || 
          notification.frontend_route.startsWith('https://')) {
        window.open(notification.frontend_route, '_blank');
      } else {
        // Internal route - use React Router
        navigate(notification.frontend_route);
      }
      onClose();
    } else {
      // Fallback: try to construct route from context
      const route = constructFallbackRoute(notification);
      if (route) {
        navigate(route);
        onClose();
      } else {
        console.warn('No valid route found for notification:', notification);
      }
    }
  },
  [markReadMutation, navigate, onClose]
);

// Add this helper function
const constructFallbackRoute = (notification) => {
  // Try to construct route from available context
  if (notification.campaign_id) {
    return `/campaigns/${notification.campaign_id}`;
  }
  if (notification.partnership_id) {
    return `/partnerships/${notification.partnership_id}`;
  }
  if (notification.conversation_id) {
    return `/join/${notification.conversation_id}`;
  }
  if (notification.organizer_id) {
    return `/organizers/${notification.organizer_id}`;
  }
  if (notification.invoice_id) {
    return `/invoicing/${notification.invoice_id}`;
  }
  if (notification.payout_id) {
    return `/payouts`;
  }
  
  // Default fallback
  return isCreator ? '/creatorconnect/start' : '/dashboard';
};
  
  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);
  
  const handleDismiss = useCallback(
    (event, notificationId) => {
      event.stopPropagation();
      dismissMutation.mutate(notificationId);
    },
    [dismissMutation]
  );
  
  const handleDelete = useCallback(
    (event, notificationId) => {
      event.stopPropagation();
      deleteMutation.mutate(notificationId);
    },
    [deleteMutation]
  );
  
  // ──────────────────────────────────────────────────────────────────────
  // TAB OPTIONS
  // ──────────────────────────────────────────────────────────────────────
  const tabOptions = useMemo(() => {
    const options = [
      { value: 'all', label: 'All', count: counts?.total_unread || 0 },
    ];
    
    if (counts?.by_source) {
      Object.entries(counts.by_source).forEach(([source, count]) => {
        options.push({
          value: source,
          label: source.charAt(0).toUpperCase() + source.slice(1),
          count,
        });
      });
    }
    
    return options;
  }, [counts]);
  
  // ──────────────────────────────────────────────────────────────────────
  // RENDER NOTIFICATION ITEM
  // ──────────────────────────────────────────────────────────────────────
  const renderNotification = (notification) => {
    const Icon =
      NOTIFICATION_ICONS[notification.notification_type] ||
      SOURCE_ICONS[notification.notification_source] ||
      NotificationsIcon;
    
    const isUnread = !notification.is_read;
    const age = notification.age_minutes;
    
    return (
      <ListItem
        key={notification.id}
        disablePadding
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: isUnread
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
          '&:hover': {
            backgroundColor: isUnread
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.action.hover, 0.08),
          },
        }}
      >
        <ListItemButton
          onClick={() => handleNotificationClick(notification)}
          sx={{ py: 1.5, px: 2 }}
        >
          {/* Icon */}
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: notification.actor?.avatar
                  ? 'transparent'
                  : alpha(theme.palette.primary.main, 0.1),
              }}
              src={notification.actor?.avatar}
            >
              <Icon fontSize="small" color="primary" />
            </Avatar>
          </ListItemIcon>
          
          {/* Content */}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isUnread ? 600 : 400,
                    flex: 1,
                  }}
                >
                  {notification.title}
                </Typography>
                
                {/* Priority indicator */}
                {notification.priority && notification.priority !== 'medium' && (
                  <Chip
                    label={notification.priority}
                    size="small"
                    color={PRIORITY_COLORS[notification.priority]}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            }
            secondary={
              <Box>
                {notification.message && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {notification.message}
                  </Typography>
                )}
                
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {formatDistanceToNow(new Date(notification.created_at), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            }
          />
          
          {/* Unread indicator */}
          {isUnread && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                ml: 1,
              }}
            />
          )}
          
          {/* Actions */}
          <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }}>
            {isUnread && (
              <Tooltip title="Mark as read">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    markReadMutation.mutate(notification.id);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => handleDelete(e, notification.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };
  
  // ──────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6">Notifications</Typography>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {counts?.total_unread > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={handleMarkAllRead}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Toggle unread only">
            <IconButton
              size="small"
              onClick={() => setShowOnlyUnread(!showOnlyUnread)}
              color={showOnlyUnread ? 'primary' : 'default'}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      {/* Tabs */}
      {tabOptions.length > 1 && (
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              py: 1,
            },
          }}
        >
          {tabOptions.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  {tab.count > 0 && (
                    <Chip
                      label={tab.count}
                      size="small"
                      color="primary"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      )}
      
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              Failed to load notifications. Please try again.
            </Alert>
          </Box>
        )}
        
        {!isLoading && !error && notifications.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              textAlign: 'center',
            }}
          >
            <NotificationsIcon
              sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              {showOnlyUnread
                ? 'No unread notifications'
                : 'No notifications yet'}
            </Typography>
          </Box>
        )}
        
        {!isLoading && !error && notifications.length > 0 && (
          <List disablePadding>
            {notifications.map(renderNotification)}
          </List>
        )}
      </Box>
      
      {/* Footer */}
      {notifications.length > 0 && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Button
            size="small"
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            sx={{ textTransform: 'none' }}
          >
            View all notifications
          </Button>
        </Box>
      )}
    </Menu>
  );
}